/* useSchema — fetch the admin-managed Plan Authoring schema (iter 97).
 *
 * Single shared cache (module-level) so all instances share the same fetch.
 * Subscribers are notified when the schema reloads (e.g. after the admin
 * editor saves changes). Falls back to the bundled DEFAULT_FALLBACK if the
 * fetch fails so the planner can still author plans during a backend
 * hiccup.
 *
 * Usage:
 *   const { schema, reload, isLoading } = useSchema();
 *   const label = schema.sections.find(s => s.id === "bite_metrics")?.label;
 *
 * Schema shape:
 *   {
 *     sections: [{id, label, visible, order, removable}],
 *     fields: { [field_name]: {label, placeholder, visible, section_id, removable} },
 *     clinical_thresholds: { arch_balance_delta, overjet_final_min, ... }
 *   }
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";


/* Mirrors backend defaults — used as a fallback if the API isn't reachable. */
export const DEFAULT_FALLBACK = {
    sections: [
        { id: "bite_metrics", label: "Bite metrics (initial → final)", visible: true, order: 1 },
        { id: "pertooth",     label: "Per-tooth plan",                  visible: true, order: 2 },
        { id: "protocol",     label: "Proposed protocol",               visible: true, order: 3 },
        { id: "wear",         label: "Aligners & wear schedule",        visible: true, order: 4 },
        { id: "retention",    label: "Retention plan",                  visible: true, order: 5 },
        { id: "deliverable",  label: "3rd-party deliverable",           visible: true, order: 6 },
    ],
    fields: {},
    clinical_thresholds: {
        arch_balance_delta: 8,
        overjet_final_min: 1.5,
        overjet_final_max: 3.0,
        overbite_final_min: 1.0,
        overbite_final_max: 2.0,
        ipr_max_per_contact: 0.5,
        ipr_total_flag: 4.0,
        wear_default_min: 7,
        wear_default_max: 21,
        retainer_recall_min_months: 3,
    },
};


let cache = null;
let inflight = null;
const subscribers = new Set();


function notify(next) {
    cache = next;
    subscribers.forEach((fn) => fn(next));
}


async function fetchSchema() {
    if (cache) return cache;
    if (inflight) return inflight;
    inflight = api.get("/plan-authoring-schema").then((r) => {
        cache = r.data;
        inflight = null;
        return cache;
    }).catch(() => {
        inflight = null;
        cache = DEFAULT_FALLBACK;
        return cache;
    });
    return inflight;
}


export default function useSchema() {
    const [schema, setSchema] = useState(cache || DEFAULT_FALLBACK);
    const [isLoading, setIsLoading] = useState(!cache);

    useEffect(() => {
        const onChange = (next) => setSchema(next);
        subscribers.add(onChange);
        if (!cache) {
            fetchSchema().then((s) => {
                setSchema(s);
                setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }
        return () => { subscribers.delete(onChange); };
    }, []);

    const reload = useCallback(async () => {
        cache = null;
        inflight = null;
        const next = await fetchSchema();
        notify(next);
        return next;
    }, []);

    /* Helper accessors for cleaner JSX in consumers. */
    const sectionLabel = useCallback((id, fallback) => {
        const s = (schema.sections || []).find((s) => s.id === id);
        if (!s) return fallback || "";
        return s.visible === false ? null : (s.label || fallback);
    }, [schema]);

    const fieldVisible = useCallback((name) => {
        const f = (schema.fields || {})[name];
        if (!f) return true;
        return f.visible !== false;
    }, [schema]);

    const fieldLabel = useCallback((name, fallback) => {
        const f = (schema.fields || {})[name];
        return (f && f.label) || fallback || name;
    }, [schema]);

    const fieldPlaceholder = useCallback((name, fallback) => {
        const f = (schema.fields || {})[name];
        if (f && typeof f.placeholder === "string" && f.placeholder.length) return f.placeholder;
        return fallback || "";
    }, [schema]);

    const threshold = useCallback((k, fallback) => {
        const t = schema.clinical_thresholds || {};
        return t[k] !== undefined ? t[k] : (fallback !== undefined ? fallback : DEFAULT_FALLBACK.clinical_thresholds[k]);
    }, [schema]);

    return {
        schema, isLoading, reload,
        sectionLabel, fieldVisible, fieldLabel, fieldPlaceholder, threshold,
    };
}
